import anime from 'animejs';
import { schedule } from '../data/schedule';
import { inforoll } from '../renderers/inforoll'
import { live } from '../data/live';
import { getWaitTime } from '../wait-time';
import { flagGeometry } from './flag-geometry';
import { incentiveFlag } from './incentive-flag-renderer';

var dayjs = require('dayjs');

var duration = require('dayjs/plugin/duration')
dayjs.extend(duration);
var relativeTime = require('dayjs/plugin/relativeTime')
dayjs.extend(relativeTime);
require('dayjs/locale/it')

let flagRenderer = {
    nextRunElement: '#flag-piece-red>.long-flag>#next-run',
    futureRuns: [],

    futureRunAnimationDelay: 20000,

    redFinished: undefined,
    whiteFinished: undefined,
    retractFinished: undefined,

    playRed: () => {
        const done = new Promise((resolve) => { flagRenderer.redFinished = resolve; });
        flagRenderer.showRed();
        return done;
    },

    playWhite: () => {
        const done = new Promise((resolve) => { flagRenderer.whiteFinished = resolve; });
        flagRenderer.showWhite();
        return done;
    },

    playRetract: () => {
        const done = new Promise((resolve) => { flagRenderer.retractFinished = resolve; });
        flagRenderer.clearFlag();
        return done;
    },

    showRed: () => {
        flagRenderer.setNextRunData();
        if (typeof schedule.nextRun === 'undefined') {
            // The driver skips this segment via when(), so reaching here means it was
            // called by hand. Just report done.
            flagRenderer.redFinished?.();
            return;
        }

        anime({
            targets: '#flag-piece-red>.long-flag',
            translateX: flagGeometry.flagOutExtent,
            skew: flagGeometry.skewProperties,
            easing: 'easeOutQuint',
            duration: flagGeometry.linearOutDuration,
            complete: flagRenderer.showNextRun,
        });
    },

    showNextRun: () => {
        anime({
            targets: flagRenderer.nextRunElement,
            skew: flagGeometry.skewPropertiesNegative,
            opacity: 1,
            translateX: 10,
            easing: 'linear',
            duration: 400,
            complete: function () {
                flagRenderer.hideNextGameIn(10);
            },
        });
    },

    showArrow1: () => {
        if (flagRenderer.futureRuns.length < 2) {
            flagRenderer.showFutureRuns();
            return;
        }

        anime({
            targets: '#arrow-1',
            opacity: 1,
            easing: 'linear',
            duration: 200,
            complete: function () {
                if (flagRenderer.futureRuns.length > 2) flagRenderer.showArrow2();
                else flagRenderer.showFutureRuns();
            },
        });
    },

    showArrow2: () => {
        anime({
            targets: '#arrow-2',
            opacity: 1,
            easing: 'linear',
            duration: 200,
            complete: flagRenderer.showFutureRuns,
        });
    },

    hideArrow1: () => {
        anime({
            targets: '#arrow-1',
            opacity: 0,
            easing: 'linear',
            duration: 200,
        });
    },

    hideArrow2: () => {
        anime({
            targets: '#arrow-2',
            opacity: 0,
            easing: 'linear',
            duration: 200,
        });
    },

    showFutureRuns: () => {
        const futureRuns = document.querySelectorAll('#future-runs>.future-run');
        let t = anime.timeline();
        futureRuns.forEach(function (runEl, index) {
            t.add({
                targets: runEl.querySelector('.game-time'),
                opacity: 1,
                translateX: 10,
                easing: 'linear',
                duration: 200,
                complete: function () {
                    anime({
                        // hides name if alternate mode is on
                        targets: runEl.querySelector('.game-name.alternate'),
                        opacity: 0,
                        easing: 'linear',
                        duration: flagGeometry.futureRunAnimationDuration,
                        direction: 'alternate',
                        loop: 6,
                        delay: flagRenderer.futureRunAnimationDelay / 12,
                        endDelay: flagRenderer.futureRunAnimationDelay / 12,
                    });
                    anime({
                        // shows category if alternate mode is on
                        targets: runEl.querySelector('.game-category.alternate'),
                        opacity: 1,
                        easing: 'linear',
                        duration: flagGeometry.futureRunAnimationDuration,
                        direction: 'alternate',
                        loop: 6,
                        delay: flagRenderer.futureRunAnimationDelay / 12,
                        endDelay: flagRenderer.futureRunAnimationDelay / 12,
                    });

                    anime({
                        targets: runEl.querySelector('.game-time'),
                        opacity: 0,
                        translateX: 0,
                        easing: 'linear',
                        duration: 200,
                        delay: flagRenderer.futureRunAnimationDelay,
                    });
                }
            })
                .add({
                    targets: runEl.querySelector('.game-data'),
                    opacity: 1,
                    translateX: 10,
                    easing: 'linear',
                    duration: 200,
                    complete: function () {
                        anime({
                            targets: runEl.querySelector('.game-data'),
                            opacity: 0,
                            translateX: 0,
                            easing: 'linear',
                            duration: 200,
                            delay: flagRenderer.futureRunAnimationDelay,
                            complete: function () {
                                if (index === futureRuns.length - 1) {
                                    flagRenderer.hideArrow1();
                                    flagRenderer.hideArrow2();
                                    flagRenderer.whiteFinished?.();
                                }
                            }
                        });
                    }
                });
        });
    },

    hideNextGame: () => {
        anime({
            targets: flagRenderer.nextRunElement,
            skew: flagGeometry.skewPropertiesNegative,
            opacity: 0,
            translateX: 10,
            easing: 'linear',
            duration: 400,
            complete: flagRenderer.resetNextGamePosition
        });
    },

    resetNextGamePosition: () => {
        anime({
            targets: flagRenderer.nextRunElement,
            skew: flagGeometry.skewPropertiesNegative,
            opacity: 0,
            translateX: 0,
            easing: 'linear',
            duration: 400,
            complete: function () {
                // The old chain paused 400 ms here before showWhite(); keep the beat so
                // the panels still change over at the same rhythm.
                setTimeout(() => flagRenderer.redFinished?.(), 400);
            }
        });
    },

    hideNextGameIn: (seconds) => {
        setTimeout(
            flagRenderer.hideNextGame,
            seconds * 1000
        );
    },

    showWhiteIn: (seconds) => {
        setTimeout(
            flagRenderer.showWhite,
            seconds * 1000
        );
    },

    setNextRunData: () => {
        const gameNameEl = document.querySelector('#flag-piece-red>.long-flag>#next-run>.game-name');
        gameNameEl.innerHTML = schedule.nextRun?.name ?? 'N/A';

        const gameCategoryEl = document.querySelector('#flag-piece-red>.long-flag>#next-run>.game-category');
        gameCategoryEl.innerHTML = schedule.nextRun?.submission?.category ?? '';

        const gameRunnerEl = document.querySelector('#flag-piece-red>.long-flag>#next-run>.game-runner>.game-runner-name');
        const runners = schedule.nextRun?.submission?.runners?.map(r => r.user?.name) ?? [];
        const runnerFormattedString = runners.join(' <span class="no-hl">e</span> ');
        gameRunnerEl.innerHTML = runnerFormattedString;

        const gameRunnerParentEl = document.querySelector('#flag-piece-red>.long-flag>#next-run>.game-runner');
        if (runnerFormattedString === '') {
            gameRunnerParentEl.classList.add('hide');
        } else {
            gameRunnerParentEl.classList.remove('hide');
        }
    },

    // Counts to the run itself, after its setup: projected_start (every overrun already
    // folded in by the backend) plus setup_time, compared straight against the real clock.
    setFutureRunData: () => {
        flagRenderer.futureRuns = schedule.next5.slice(1, 4);
        const futureRuns = flagRenderer.futureRuns;
        // console.log(futureRuns);
        const runElements = document.querySelectorAll('#future-runs>.future-run');
        runElements.forEach((runElement, i) => {
            const run = futureRuns[i];
            let shouldClear = false;
            if (typeof run === 'undefined') shouldClear = true;
            // console.log(typeof run, run);

            let waitTimeLabel = [];
            const futureRunDurationSelector = runElement.querySelector('.game-time');
            const gameTimeInEl = futureRunDurationSelector.querySelector('.game-time-in');
            if (shouldClear) {
                gameTimeInEl.innerHTML = "";
            } else {
                waitTimeLabel = getWaitTime(schedule.getRunStartDate(run));
                gameTimeInEl.innerHTML = waitTimeLabel.label;
            }

            const gameTimeEstimateEl = futureRunDurationSelector.querySelector('.game-time-estimate');
            if (shouldClear) {
                gameTimeEstimateEl.innerHTML = "";
            } else {
                gameTimeEstimateEl.innerHTML = waitTimeLabel.duration;
            }

            const futureRunSelector = runElement.querySelector('.game-data');

            const gameNameEl = futureRunSelector.querySelector('.game-name');
            if (shouldClear) {
                gameNameEl.innerHTML = "";
            } else {
                gameNameEl.innerHTML = run.name ?? 'N/A';
            }

            const gameCategoryEl = futureRunSelector.querySelector('.game-category');
            const categoryName = run?.submission?.category ?? '';
            if (shouldClear) {
                gameCategoryEl.innerHTML = "";
            } else {
                gameCategoryEl.innerHTML = categoryName;
            }

            gameNameEl.classList.remove('alternate');
            gameCategoryEl.classList.remove('alternate');
            gameNameEl.style.opacity = "";
            gameCategoryEl.style.opacity = "";

            if (categoryName !== '' && gameNameEl.getBoundingClientRect().top !== gameCategoryEl.getBoundingClientRect().top) {
                gameNameEl.classList.add('alternate');
                gameCategoryEl.classList.add('alternate');
            }

            const gameRunnerNameEl = futureRunSelector.querySelector('.game-runner>.game-runner-name');
            const gameRunnerEl = futureRunSelector.querySelector('.game-runner');
            if (shouldClear) {
                gameRunnerNameEl.innerHTML = "";
                gameRunnerEl.classList.add('hide');
            } else {
                const runners = run.submission?.runners?.map(r => r.user?.name) ?? [];
                const runnerFormattedString = runners.join(' <span class="no-hl">e</span> ');
                gameRunnerNameEl.innerHTML = runnerFormattedString;
                gameRunnerEl.classList.remove('hide');

                if (runnerFormattedString === '') {
                    gameRunnerEl.classList.add('hide');
                }
            }
        });
    },

    showWhite: () => {
        flagRenderer.setFutureRunData();
        if (flagRenderer.futureRuns.length < 1) {
            flagRenderer.whiteFinished?.();
            return;
        }

        anime({
            targets: ['#flag-piece-white>.long-flag', '#flag-piece-red>.long-flag'],
            translateX: flagGeometry.flagOutExtent,
            skew: flagGeometry.skewProperties,
            easing: 'easeOutQuint',
            duration: flagGeometry.linearOutDuration,
            complete: flagRenderer.showArrow1,
        });
    },

    clearFlag: () => {
        anime({
            targets: '#arrow-incentive',
            translateX: 0,
            opacity: 0,
            skew: flagGeometry.skewPropertiesNegative,
            easing: 'linear',
            duration: flagGeometry.futureRunAnimationDuration,
        });
        anime.timeline()
            .add({
                targets: '#flag-piece-green>.long-flag',
                translateX: flagGeometry.flagOutExtent + 20,
                skew: flagGeometry.skewProperties,
                easing: 'easeOutCubic',
                duration: flagGeometry.linearOutDuration * 2,
            })
            .add({
                targets: '#flag-piece-green>.long-flag',
                translateX: flagGeometry.flagOutExtent,
                skew: flagGeometry.skewProperties,
                easing: 'easeInCubic',
                duration: 750 * 0.8,
            })
            .add({
                targets: '#flag-piece-red>.long-flag',
                translateX: flagGeometry.flagOutExtent,
                skew: flagGeometry.skewProperties,
                duration: 1 * 0.8,
            }, '-=750')
            .add({
                targets: '#flag-piece-white>.long-flag',
                translateX: flagGeometry.flagOutExtent,
                skew: flagGeometry.skewProperties,
                duration: 1 * 0.8,
            }, '-=750')
            .add({
                targets: '.long-flag',
                translateX: 0,
                skew: flagGeometry.skewProperties,
                duration: 2350 * 0.8,
                easing: 'easeOutCubic',
                complete: function () {
                    schedule.refreshSchedule();
                    flagRenderer.retractFinished?.();
                }
            });
    },


}

window.flagRenderer = flagRenderer;
export { flagRenderer };